/**
 * Deadlines - firm-wide compliance dashboard.
 *
 * Five view modes (Board, Calendar, Timeline, By Client, List) sit on top of
 * the same dataset returned by GET /deadlines. The page is built around a
 * "Today's Focus" strip + smart stat tiles so a firm user immediately sees
 * what needs attention before drilling into any view.
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Search, Calendar as CalendarIcon, List, RefreshCw, Loader2, CheckCircle2,
  AlertTriangle, Clock, CalendarDays, Building2, ChevronLeft, ChevronRight,
  ChevronDown, X, Sparkles, TrendingUp, Flame, Layers, Users, Bell,
  Target, Zap,
} from 'lucide-react';
import apiClient from '@/services/api/client';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { Deadline, DeadlineType, DeadlineStatus } from '@/types/client';
import {
  DEADLINE_TYPE_LABELS, DEADLINE_STATUS_LABELS, RECURRENCE_LABELS,
} from '@/types/client';

// =====================================================================
// Helpers
// =====================================================================

const STATUS_BG: Record<DeadlineStatus, string> = {
  overdue: 'bg-red-500/10 text-red-600 border-red-500/30',
  due_soon: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  upcoming: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  completed: 'bg-green-500/10 text-green-600 border-green-500/30',
};

const STATUS_DOT: Record<DeadlineStatus, string> = {
  overdue: 'bg-red-500',
  due_soon: 'bg-amber-500',
  upcoming: 'bg-blue-500',
  completed: 'bg-green-500',
};

const STATUS_ICON: Record<DeadlineStatus, React.ReactNode> = {
  overdue: <AlertTriangle className="h-3.5 w-3.5" />,
  due_soon: <Clock className="h-3.5 w-3.5" />,
  upcoming: <CalendarDays className="h-3.5 w-3.5" />,
  completed: <CheckCircle2 className="h-3.5 w-3.5" />,
};

function formatDayLabel(dateStr: string) {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(date);
  dueDate.setHours(0, 0, 0, 0);
  const diffDays = Math.round((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} overdue`;
  if (diffDays <= 7) return `In ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function formatFullDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function diffDaysFromToday(dateStr: string) {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

/**
 * Plain-English reasoning for the deadline's current status. Built from the
 * ATO due date + entity type so users understand exactly *why* the system
 * flagged something - no black-box "overdue" badge.
 */
function explainStatus(deadline: Deadline): { headline: string; details: string[] } {
  const days = diffDaysFromToday(deadline.dueDate);
  const dueDateStr = formatFullDate(deadline.dueDate);
  const entityType = (deadline as any).clientType
    ? String((deadline as any).clientType).replace(/_/g, ' ')
    : 'this client';
  const typeLabel = DEADLINE_TYPE_LABELS[deadline.deadlineType] || 'this obligation';

  switch (deadline.status) {
    case 'overdue': {
      const absDays = Math.abs(days);
      return {
        headline: `${absDays} day${absDays === 1 ? '' : 's'} past the ATO due date`,
        details: [
          `ATO due date for ${typeLabel}: ${dueDateStr}`,
          `Today is ${absDays} day${absDays === 1 ? '' : 's'} past that date.`,
          `Applies to ${entityType} entities under the ATO compliance calendar.`,
          `Status will clear once you mark this lodged.`,
        ],
      };
    }
    case 'due_soon':
      return {
        headline: `Due in ${days} day${days === 1 ? '' : 's'} - within the 14-day warning window`,
        details: [
          `ATO due date for ${typeLabel}: ${dueDateStr}`,
          `Items are flagged "Due soon" when ≤ 14 days remain.`,
          `Lodge before the due date to avoid late-lodgement penalties.`,
        ],
      };
    case 'upcoming':
      return {
        headline: `Scheduled by the ATO compliance calendar`,
        details: [
          `ATO due date for ${typeLabel}: ${dueDateStr}`,
          `Auto-added because ${entityType} entities are subject to this obligation.`,
          `Will move to "Due soon" 14 days before the due date.`,
        ],
      };
    case 'completed':
      return {
        headline: 'Marked completed',
        details: [
          `ATO due date was: ${dueDateStr}`,
          (deadline as any).lodgementReference
            ? `Lodgement reference: ${(deadline as any).lodgementReference}`
            : 'No lodgement reference recorded.',
        ],
      };
  }
}

// =====================================================================
// Page
// =====================================================================

export function DeadlinesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusChip, setStatusChip] = useState<DeadlineStatus | 'all'>(
    (searchParams.get('status') as DeadlineStatus) || 'all'
  );
  const [typeFilter, setTypeFilter] = useState<DeadlineType | 'all'>('all');
  const [view, setView] = useState<'board' | 'calendar' | 'timeline' | 'byclient' | 'list'>(
    (searchParams.get('view') as any) || 'board'
  );
  const [selectedDeadline, setSelectedDeadline] = useState<Deadline | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [lodgementReference, setLodgementReference] = useState('');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  const { data: deadlinesResponse, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['deadlines', search, statusChip, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusChip !== 'all') params.set('status', statusChip);
      if (typeFilter !== 'all') params.set('deadlineType', typeFilter);
      const r = await apiClient.get(`/deadlines?${params.toString()}`);
      return r.data.data;
    },
  });

  const deadlines: Deadline[] = deadlinesResponse?.deadlines || [];
  const stats = deadlinesResponse?.stats || { overdue: 0, dueSoon: 0, upcoming: 0, completed: 0, total: 0 };

  // ========== Derived insights ==========

  const insights = useMemo(() => {
    const overdueItems = deadlines.filter(d => d.status === 'overdue');
    const biggestOverdue = overdueItems.reduce<Deadline | null>((acc, d) => {
      const days = -diffDaysFromToday(d.dueDate);
      if (!acc || days > -diffDaysFromToday(acc.dueDate)) return d;
      return acc;
    }, null);

    // Clients with multiple overdue items
    const overdueByClient = new Map<string, number>();
    for (const d of overdueItems) {
      overdueByClient.set(d.clientId, (overdueByClient.get(d.clientId) || 0) + 1);
    }
    const hotClients = Array.from(overdueByClient.entries())
      .filter(([, n]) => n >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id, count]) => ({
        clientId: id,
        clientName: overdueItems.find(d => d.clientId === id)?.clientName || 'Unknown',
        count,
      }));

    // This week count
    const thisWeek = deadlines.filter(d => {
      const days = diffDaysFromToday(d.dueDate);
      return d.status !== 'completed' && days >= 0 && days <= 7;
    });

    // On-time rate (completed vs total in last FY)
    const totalCompletable = stats.overdue + stats.dueSoon + stats.upcoming + stats.completed;
    const onTimeRate = totalCompletable === 0
      ? 100
      : Math.round((stats.completed / totalCompletable) * 100);

    return {
      biggestOverdue,
      biggestOverdueDays: biggestOverdue ? -diffDaysFromToday(biggestOverdue.dueDate) : 0,
      hotClients,
      thisWeekCount: thisWeek.length,
      onTimeRate,
    };
  }, [deadlines, stats]);

  // Today's Focus = top 6 most urgent (overdue + due soon)
  const todaysFocus = useMemo(() =>
    [...deadlines]
      .filter(d => d.status === 'overdue' || d.status === 'due_soon')
      .sort((a, b) => {
        if (a.status === 'overdue' && b.status !== 'overdue') return -1;
        if (b.status === 'overdue' && a.status !== 'overdue') return 1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      })
      .slice(0, 6),
    [deadlines]);

  // Group deadlines by date for calendar
  const deadlinesByDate = useMemo(() => {
    const grouped: Record<string, Deadline[]> = {};
    for (const d of deadlines) {
      const dateKey = (d.dueDate?.split('T')[0]) || d.dueDate;
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(d);
    }
    return grouped;
  }, [deadlines]);

  // Group by client
  const byClient = useMemo(() => {
    const m = new Map<string, { clientId: string; clientName: string; clientType: string; items: Deadline[] }>();
    for (const d of deadlines) {
      const k = d.clientId;
      if (!m.has(k)) {
        m.set(k, {
          clientId: d.clientId,
          clientName: d.clientName,
          clientType: (d as any).clientType || 'unknown',
          items: [],
        });
      }
      m.get(k)!.items.push(d);
    }
    return Array.from(m.values()).sort((a, b) => {
      // clients with more overdue items first
      const ao = a.items.filter(x => x.status === 'overdue').length;
      const bo = b.items.filter(x => x.status === 'overdue').length;
      if (ao !== bo) return bo - ao;
      return a.clientName.localeCompare(b.clientName);
    });
  }, [deadlines]);

  // Buckets for board
  const board = useMemo(() => ({
    overdue: deadlines.filter(d => d.status === 'overdue'),
    due_soon: deadlines.filter(d => d.status === 'due_soon'),
    upcoming: deadlines.filter(d => d.status === 'upcoming'),
    completed: deadlines.filter(d => d.status === 'completed'),
  }), [deadlines]);

  // Timeline buckets - next 30 days
  const timeline = useMemo(() => {
    const days: Array<{ date: Date; key: string; items: Deadline[]; label: string; isToday: boolean }> = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    for (let i = -7; i <= 30; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      const key = d.toISOString().split('T')[0];
      days.push({
        date: d,
        key,
        items: deadlinesByDate[key] || [],
        label: i === 0 ? 'Today' : i === 1 ? 'Tmrw' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
        isToday: i === 0,
      });
    }
    return days;
  }, [deadlinesByDate]);

  const completeMutation = useMutation({
    mutationFn: async ({ id, reference }: { id: string; reference?: string }) => {
      const r = await apiClient.post(`/deadlines/${encodeURIComponent(id)}/complete`, {
        lodgementReference: reference || null,
      });
      return r.data?.data || r.data;
    },
    onSuccess: () => {
      toast.success('Deadline marked as completed');
      setCompleteDialogOpen(false);
      setLodgementReference('');
      setSelectedDeadline(null);
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to mark completed');
    },
  });

  const uncompleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.delete(`/deadlines/${encodeURIComponent(id)}/complete`);
      return r.data?.data || r.data;
    },
    onSuccess: () => {
      toast.success('Marked as not completed');
      setSelectedDeadline(null);
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to un-mark');
    },
  });

  const updateView = (v: typeof view) => {
    setView(v);
    const next = new URLSearchParams(searchParams);
    next.set('view', v);
    setSearchParams(next, { replace: true });
  };

  const updateStatus = (s: DeadlineStatus | 'all') => {
    setStatusChip(s);
    const next = new URLSearchParams(searchParams);
    if (s === 'all') next.delete('status'); else next.set('status', s);
    setSearchParams(next, { replace: true });
  };

  const handleQuickComplete = (d: Deadline) => {
    setSelectedDeadline(d);
    setCompleteDialogOpen(true);
  };

  const goToClient = (clientId: string) => navigate(`/panel/files/clients/${clientId}`);

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* ===== Hero Header ===== */}
        <div className="border-b bg-gradient-to-br from-[#5048e5]/10 via-background to-background">
          <div className="px-6 py-4">
            <PageHeader
              icon={CalendarDays}
              title="Compliance Deadlines"
              description="Every ATO obligation across every client - at a glance"
              className="mb-3"
              actions={
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <RefreshCw className={cn('h-3.5 w-3.5 mr-1', isFetching && 'animate-spin')} />
                    Refresh
                  </Button>
                </div>
              }
            />

            {/* Insights ribbon */}
            <div className="flex flex-wrap items-center gap-2 text-xs mt-2">
              {stats.overdue > 0 ? (
                <Badge className="bg-red-500/10 text-red-600 border-red-500/30 gap-1.5 py-1 px-2.5">
                  <Flame className="h-3 w-3" />
                  {stats.overdue} overdue
                  {insights.biggestOverdue && ` · biggest is ${insights.biggestOverdueDays} days`}
                </Badge>
              ) : (
                <Badge className="bg-green-500/10 text-green-600 border-green-500/30 gap-1.5 py-1 px-2.5">
                  <Sparkles className="h-3 w-3" />
                  Nothing overdue - keep it up
                </Badge>
              )}
              <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/30 gap-1.5 py-1 px-2.5">
                <Bell className="h-3 w-3" />
                {insights.thisWeekCount} due this week
              </Badge>
              <Badge className="bg-[#5048e5]/10 text-[#5048e5] border-[#5048e5]/30 gap-1.5 py-1 px-2.5">
                <Target className="h-3 w-3" />
                On-time rate: {insights.onTimeRate}%
              </Badge>
              {insights.hotClients.length > 0 && (
                <Badge variant="outline" className="gap-1.5 py-1 px-2.5">
                  <Users className="h-3 w-3" />
                  Hot clients: {insights.hotClients.map(h => h.clientName).join(', ')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* ===== Stat tiles ===== */}
        <div className="px-6 py-4 border-b">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatTile
              icon={<AlertTriangle className="h-5 w-5" />}
              label="Overdue"
              value={stats.overdue}
              tone="red"
              active={statusChip === 'overdue'}
              onClick={() => updateStatus(statusChip === 'overdue' ? 'all' : 'overdue')}
            />
            <StatTile
              icon={<Clock className="h-5 w-5" />}
              label="Due Soon"
              value={stats.dueSoon}
              tone="amber"
              active={statusChip === 'due_soon'}
              onClick={() => updateStatus(statusChip === 'due_soon' ? 'all' : 'due_soon')}
            />
            <StatTile
              icon={<CalendarDays className="h-5 w-5" />}
              label="Upcoming"
              value={stats.upcoming}
              tone="blue"
              active={statusChip === 'upcoming'}
              onClick={() => updateStatus(statusChip === 'upcoming' ? 'all' : 'upcoming')}
            />
            <StatTile
              icon={<CheckCircle2 className="h-5 w-5" />}
              label="Completed"
              value={stats.completed}
              tone="green"
              active={statusChip === 'completed'}
              onClick={() => updateStatus(statusChip === 'completed' ? 'all' : 'completed')}
            />
            <StatTile
              icon={<Layers className="h-5 w-5" />}
              label="All deadlines"
              value={stats.total}
              tone="slate"
              active={statusChip === 'all'}
              onClick={() => updateStatus('all')}
            />
          </div>
        </div>

        {/* ===== Today's Focus strip ===== */}
        {todaysFocus.length > 0 && (
          <div className="px-6 py-4 border-b bg-muted/20">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-amber-500" />
              <h3 className="text-sm font-semibold">Today's Focus</h3>
              <span className="text-[11px] text-muted-foreground">
                Most urgent {todaysFocus.length} item{todaysFocus.length === 1 ? '' : 's'}
              </span>
            </div>
            <ScrollArea>
              <div className="flex gap-3 pb-3">
                {todaysFocus.map(d => (
                  <FocusCard
                    key={d.id}
                    deadline={d}
                    onClick={() => setSelectedDeadline(d)}
                    onComplete={() => handleQuickComplete(d)}
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}

        {/* ===== Filters + view tabs ===== */}
        <div className="flex items-center gap-3 px-6 py-3 border-b bg-background">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by client or deadline…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as DeadlineType | 'all')}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(DEADLINE_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Tabs value={view} onValueChange={(v) => updateView(v as any)} className="ml-auto">
            <TabsList>
              <TabsTrigger value="board" className="gap-1.5"><Layers className="h-3.5 w-3.5" /> Board</TabsTrigger>
              <TabsTrigger value="calendar" className="gap-1.5"><CalendarIcon className="h-3.5 w-3.5" /> Calendar</TabsTrigger>
              <TabsTrigger value="timeline" className="gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Timeline</TabsTrigger>
              <TabsTrigger value="byclient" className="gap-1.5"><Users className="h-3.5 w-3.5" /> By Client</TabsTrigger>
              <TabsTrigger value="list" className="gap-1.5"><List className="h-3.5 w-3.5" /> List</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* ===== Content area ===== */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
            </div>
          ) : view === 'board' ? (
            <BoardView
              board={board}
              onSelect={setSelectedDeadline}
              onComplete={handleQuickComplete}
            />
          ) : view === 'calendar' ? (
            <CalendarView
              calendarDate={calendarDate}
              setCalendarDate={setCalendarDate}
              deadlinesByDate={deadlinesByDate}
              onSelect={setSelectedDeadline}
            />
          ) : view === 'timeline' ? (
            <TimelineView timeline={timeline} onSelect={setSelectedDeadline} onComplete={handleQuickComplete} />
          ) : view === 'byclient' ? (
            <ByClientView byClient={byClient} onSelect={setSelectedDeadline} onComplete={handleQuickComplete} onClient={goToClient} />
          ) : (
            <ListView deadlines={deadlines} onSelect={setSelectedDeadline} onComplete={handleQuickComplete} />
          )}
        </div>
      </div>

      {/* ===== Detail panel ===== */}
      {selectedDeadline && (
        <DetailPanel
          deadline={selectedDeadline}
          onClose={() => setSelectedDeadline(null)}
          onComplete={() => handleQuickComplete(selectedDeadline)}
          onUncomplete={() => {
            if (confirm('Un-mark this deadline as completed?')) {
              uncompleteMutation.mutate(selectedDeadline.id);
            }
          }}
          uncompleting={uncompleteMutation.isPending}
          onGoToClient={() => goToClient(selectedDeadline.clientId)}
        />
      )}

      {/* ===== Complete Dialog ===== */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Mark as completed
            </DialogTitle>
            <DialogDescription>
              {selectedDeadline?.title} for {selectedDeadline?.clientName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">ATO Lodgement Reference (optional)</label>
              <Input
                value={lodgementReference}
                onChange={(e) => setLodgementReference(e.target.value)}
                placeholder="e.g., BAS-2024-Q2-12345"
                className="mt-1.5"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Saved for audit trail. You can update this later.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => selectedDeadline && completeMutation.mutate({
                id: selectedDeadline.id,
                reference: lodgementReference || undefined,
              })}
              disabled={completeMutation.isPending}
            >
              {completeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              Mark Completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

function StatTile({
  icon, label, value, tone, active, onClick,
}: {
  icon: React.ReactNode; label: string; value: number;
  tone: 'red' | 'amber' | 'blue' | 'green' | 'slate';
  active?: boolean; onClick?: () => void;
}) {
  const TONES: Record<string, { bg: string; ring: string; ic: string }> = {
    red:   { bg: 'from-red-500/10 to-red-500/0',   ring: 'ring-red-500/40',   ic: 'text-red-600' },
    amber: { bg: 'from-amber-500/10 to-amber-500/0', ring: 'ring-amber-500/40', ic: 'text-amber-600' },
    blue:  { bg: 'from-blue-500/10 to-blue-500/0', ring: 'ring-blue-500/40',  ic: 'text-blue-600' },
    green: { bg: 'from-green-500/10 to-green-500/0', ring: 'ring-green-500/40', ic: 'text-green-600' },
    slate: { bg: 'from-slate-500/10 to-slate-500/0', ring: 'ring-slate-500/40', ic: 'text-slate-600' },
  };
  const t = TONES[tone];
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-gradient-to-br p-3 text-left transition-all hover:shadow-md',
        t.bg,
        active ? `ring-2 ${t.ring}` : 'ring-0',
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={cn('p-1.5 rounded-md bg-background/70', t.ic)}>{icon}</div>
        {active && <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Active</span>}
      </div>
      <p className="text-3xl font-bold leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </button>
  );
}

function FocusCard({ deadline, onClick, onComplete }: {
  deadline: Deadline; onClick: () => void; onComplete: () => void;
}) {
  const days = diffDaysFromToday(deadline.dueDate);
  const isOverdue = deadline.status === 'overdue';
  return (
    <div
      className={cn(
        'min-w-[280px] max-w-[300px] rounded-lg border p-3 cursor-pointer transition-all hover:shadow-md',
        isOverdue ? 'bg-red-500/5 border-red-500/30' : 'bg-amber-500/5 border-amber-500/30'
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-1.5">
        <Badge variant="outline" className={cn('text-[10px] gap-1', STATUS_BG[deadline.status])}>
          {STATUS_ICON[deadline.status]}
          {DEADLINE_STATUS_LABELS[deadline.status]}
        </Badge>
        <span className={cn(
          'text-[11px] font-bold tabular-nums',
          isOverdue ? 'text-red-600' : 'text-amber-700'
        )}>
          {isOverdue ? `${-days}d overdue` : days === 0 ? 'TODAY' : `${days}d left`}
        </span>
      </div>
      <p className="font-semibold text-sm truncate">{deadline.title}</p>
      <p className="text-[11px] text-muted-foreground truncate mb-2">{deadline.clientName}</p>
      <Button
        size="sm" variant="outline" className="w-full h-7 text-[11px]"
        onClick={(e) => { e.stopPropagation(); onComplete(); }}
      >
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Mark complete
      </Button>
    </div>
  );
}

function DeadlineRow({ deadline, onClick, onComplete, dense = false }: {
  deadline: Deadline; onClick: () => void; onComplete: () => void; dense?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border p-2.5 cursor-pointer transition-colors hover:bg-muted/40',
        dense && 'p-2'
      )}
      onClick={onClick}
    >
      <span className={cn('h-2 w-2 rounded-full shrink-0', STATUS_DOT[deadline.status])} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{deadline.title}</p>
          <Badge variant="outline" className="text-[10px] shrink-0">
            {DEADLINE_TYPE_LABELS[deadline.deadlineType]}
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground truncate">
          {deadline.clientName} · {formatDayLabel(deadline.dueDate)}
        </p>
      </div>
      {deadline.status !== 'completed' && (
        <Button
          size="sm" variant="ghost" className="h-7 px-2 text-[11px] shrink-0"
          onClick={(e) => { e.stopPropagation(); onComplete(); }}
        >
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
          Done
        </Button>
      )}
    </div>
  );
}

// ----- Board (Kanban) View -----

function BoardView({ board, onSelect, onComplete }: {
  board: Record<DeadlineStatus, Deadline[]>;
  onSelect: (d: Deadline) => void;
  onComplete: (d: Deadline) => void;
}) {
  const cols: Array<{ key: DeadlineStatus; title: string; tone: string; icon: React.ReactNode }> = [
    { key: 'overdue',   title: 'Overdue',   tone: 'border-red-500/40 bg-red-500/[.04]',   icon: <AlertTriangle className="h-4 w-4 text-red-500" /> },
    { key: 'due_soon',  title: 'Due soon',  tone: 'border-amber-500/40 bg-amber-500/[.04]', icon: <Clock className="h-4 w-4 text-amber-500" /> },
    { key: 'upcoming',  title: 'Upcoming',  tone: 'border-blue-500/40 bg-blue-500/[.04]',  icon: <CalendarDays className="h-4 w-4 text-blue-500" /> },
    { key: 'completed', title: 'Completed', tone: 'border-green-500/40 bg-green-500/[.04]', icon: <CheckCircle2 className="h-4 w-4 text-green-500" /> },
  ];
  return (
    <div className="h-full overflow-auto p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 min-h-full">
        {cols.map(col => (
          <div key={col.key} className={cn('rounded-lg border p-3 flex flex-col min-h-[200px]', col.tone)}>
            <div className="flex items-center gap-2 mb-3">
              {col.icon}
              <h3 className="text-sm font-semibold">{col.title}</h3>
              <Badge variant="secondary" className="ml-auto">{board[col.key].length}</Badge>
            </div>
            <ScrollArea className="flex-1 -mr-2 pr-2">
              <div className="space-y-2">
                {board[col.key].length === 0 ? (
                  <p className="text-[11px] text-center text-muted-foreground py-6 italic">
                    {col.key === 'overdue' ? '🎉 Nothing overdue' :
                     col.key === 'due_soon' ? 'No items due in next 14 days' :
                     col.key === 'completed' ? 'Items appear here when marked complete' :
                     'No upcoming items'}
                  </p>
                ) : board[col.key].slice(0, 50).map(d => (
                  <DeadlineRow
                    key={d.id} deadline={d} dense
                    onClick={() => onSelect(d)}
                    onComplete={() => onComplete(d)}
                  />
                ))}
                {board[col.key].length > 50 && (
                  <p className="text-[11px] text-center text-muted-foreground py-1">
                    +{board[col.key].length - 50} more - switch to List view to see all
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Calendar View -----

function CalendarView({ calendarDate, setCalendarDate, deadlinesByDate, onSelect }: {
  calendarDate: Date;
  setCalendarDate: (d: Date) => void;
  deadlinesByDate: Record<string, Deadline[]>;
  onSelect: (d: Deadline) => void;
}) {
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days: React.ReactNode[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`e-${i}`} className="h-28 rounded-md border bg-muted/10" />);
  }
  const today = new Date();
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const key = date.toISOString().split('T')[0];
    const items = deadlinesByDate[key] || [];
    const isToday = date.toDateString() === today.toDateString();
    const counts = items.reduce<Record<DeadlineStatus, number>>((a, d) => {
      a[d.status] = (a[d.status] || 0) + 1; return a;
    }, { overdue: 0, due_soon: 0, upcoming: 0, completed: 0 });
    days.push(
      <div
        key={day}
        className={cn(
          'h-28 rounded-md border p-1.5 flex flex-col transition-colors',
          isToday && 'bg-[#5048e5]/5 border-[#5048e5]/40 shadow-sm'
        )}
      >
        <div className="flex items-center justify-between">
          <span className={cn('text-sm font-medium', isToday && 'text-[#5048e5] font-bold')}>{day}</span>
          {items.length > 0 && (
            <div className="flex items-center gap-0.5">
              {(['overdue', 'due_soon', 'upcoming', 'completed'] as DeadlineStatus[]).map(s =>
                counts[s] > 0 && <span key={s} className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[s])} />
              )}
            </div>
          )}
        </div>
        <div className="flex-1 mt-1 space-y-0.5 overflow-hidden">
          {items.slice(0, 2).map(d => (
            <button
              key={d.id}
              onClick={() => onSelect(d)}
              className={cn(
                'w-full text-[10px] truncate rounded px-1 py-0.5 text-left border',
                STATUS_BG[d.status]
              )}
            >
              {d.title}
            </button>
          ))}
          {items.length > 2 && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="text-[10px] text-[#5048e5] font-medium pl-1 hover:underline w-full text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  +{items.length - 2} more →
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  {date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
                  <span className="ml-1.5 normal-case font-normal text-foreground">
                    · {items.length} deadline{items.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ScrollArea className="max-h-72">
                  <div className="space-y-1">
                    {items.map(d => (
                      <button
                        key={d.id}
                        onClick={() => onSelect(d)}
                        className={cn(
                          'w-full flex items-start gap-2 rounded-md border p-1.5 text-left transition-colors hover:bg-muted/30',
                          STATUS_BG[d.status]
                        )}
                      >
                        <span className={cn('h-1.5 w-1.5 rounded-full mt-1 shrink-0', STATUS_DOT[d.status])} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{d.title}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{d.clientName}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {calendarDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCalendarDate(new Date(year, month - 1, 1))}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-8" onClick={() => setCalendarDate(new Date())}>Today</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCalendarDate(new Date(year, month + 1, 1))}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="text-center text-[11px] font-medium text-muted-foreground py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">{days}</div>
      </div>
    </div>
  );
}

// ----- Timeline View -----

function TimelineView({ timeline, onSelect, onComplete }: {
  timeline: Array<{ date: Date; key: string; items: Deadline[]; label: string; isToday: boolean }>;
  onSelect: (d: Deadline) => void;
  onComplete: (d: Deadline) => void;
}) {
  return (
    <div className="h-full overflow-auto p-4 space-y-2">
      {timeline.filter(d => d.items.length > 0).map(d => (
        <div key={d.key} className={cn(
          'rounded-lg border p-3',
          d.isToday && 'bg-[#5048e5]/5 border-[#5048e5]/40'
        )}>
          <div className="flex items-center gap-2 mb-2">
            <span className={cn(
              'inline-flex items-center justify-center text-[10px] font-bold rounded-md px-2 py-0.5',
              d.isToday ? 'bg-[#5048e5] text-white' : 'bg-muted text-muted-foreground'
            )}>
              {d.label.toUpperCase()}
            </span>
            <p className="text-xs text-muted-foreground">
              {d.date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <Badge variant="secondary" className="ml-auto">{d.items.length}</Badge>
          </div>
          <div className="space-y-1.5">
            {d.items.map(item => (
              <DeadlineRow key={item.id} deadline={item} dense onClick={() => onSelect(item)} onComplete={() => onComplete(item)} />
            ))}
          </div>
        </div>
      ))}
      {timeline.every(d => d.items.length === 0) && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          <CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-50" />
          No deadlines in the next 30 days
        </CardContent></Card>
      )}
    </div>
  );
}

// ----- By-Client View -----

function ByClientView({ byClient, onSelect, onComplete, onClient }: {
  byClient: Array<{ clientId: string; clientName: string; clientType: string; items: Deadline[] }>;
  onSelect: (d: Deadline) => void;
  onComplete: (d: Deadline) => void;
  onClient: (id: string) => void;
}) {
  return (
    <div className="h-full overflow-auto p-4 space-y-2">
      {byClient.map(c => {
        const overdue = c.items.filter(d => d.status === 'overdue').length;
        const dueSoon = c.items.filter(d => d.status === 'due_soon').length;
        return (
          <Collapsible key={c.clientId} defaultOpen={overdue > 0} className="group">
            <Card className={cn(overdue > 0 && 'border-red-500/30')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/30 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-md bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center shrink-0">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-sm truncate">{c.clientName}</CardTitle>
                        <CardDescription className="text-[11px]">
                          {c.clientType.replace(/_/g, ' ')} · {c.items.length} deadline{c.items.length === 1 ? '' : 's'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {overdue > 0 && <Badge className="bg-red-500/10 text-red-600 border-red-500/30">{overdue} overdue</Badge>}
                      {dueSoon > 0 && <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">{dueSoon} due soon</Badge>}
                      <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={(e) => { e.stopPropagation(); onClient(c.clientId); }}>
                        Open client
                      </Button>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-1.5">
                  {c.items.map(item => (
                    <DeadlineRow key={item.id} deadline={item} dense onClick={() => onSelect(item)} onComplete={() => onComplete(item)} />
                  ))}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        );
      })}
      {byClient.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No clients with deadlines</CardContent></Card>
      )}
    </div>
  );
}

// ----- List View -----

function ListView({ deadlines, onSelect, onComplete }: {
  deadlines: Deadline[];
  onSelect: (d: Deadline) => void;
  onComplete: (d: Deadline) => void;
}) {
  if (deadlines.length === 0) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">
        <CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-50" />
        No deadlines match the current filters
      </div>
    );
  }
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-1.5 max-w-5xl mx-auto">
        {deadlines.map(d => (
          <DeadlineRow key={d.id} deadline={d} onClick={() => onSelect(d)} onComplete={() => onComplete(d)} />
        ))}
      </div>
    </ScrollArea>
  );
}

// ----- Detail Panel -----

function DetailPanel({ deadline, onClose, onComplete, onUncomplete, uncompleting, onGoToClient }: {
  deadline: Deadline;
  onClose: () => void;
  onComplete: () => void;
  onUncomplete: () => void;
  uncompleting?: boolean;
  onGoToClient: () => void;
}) {
  return (
    <div className="w-96 border-l bg-card flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h2 className="font-semibold">Deadline Details</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={cn('text-xs gap-1', STATUS_BG[deadline.status])}>
              {STATUS_ICON[deadline.status]}
              <span>{DEADLINE_STATUS_LABELS[deadline.status]}</span>
            </Badge>
            <Badge variant="outline">{DEADLINE_TYPE_LABELS[deadline.deadlineType]}</Badge>
          </div>
          <div>
            <h3 className="text-lg font-semibold">{deadline.title}</h3>
            {deadline.description && (
              <p className="text-sm text-muted-foreground mt-1">{deadline.description}</p>
            )}
          </div>

          {/* Why is this {status}? - plain-English reasoning */}
          {(() => {
            const { headline, details } = explainStatus(deadline);
            const tone =
              deadline.status === 'overdue' ? 'border-red-500/30 bg-red-500/5'
              : deadline.status === 'due_soon' ? 'border-amber-500/30 bg-amber-500/5'
              : deadline.status === 'completed' ? 'border-green-500/30 bg-green-500/5'
              : 'border-blue-500/30 bg-blue-500/5';
            return (
              <div className={cn('rounded-md border p-3 space-y-1.5', tone)}>
                <p className="text-xs font-semibold flex items-center gap-1.5">
                  {STATUS_ICON[deadline.status]}
                  Why is this {DEADLINE_STATUS_LABELS[deadline.status]}?
                </p>
                <p className="text-sm font-medium">{headline}</p>
                <ul className="text-[11px] text-muted-foreground space-y-0.5 list-disc pl-4">
                  {details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            );
          })()}

          <Separator />
          <button
            onClick={onGoToClient}
            className="flex items-center gap-3 w-full text-left hover:bg-muted/40 rounded-md p-2 -mx-2"
          >
            <div className="h-8 w-8 rounded-md bg-[#5048e5]/10 text-[#5048e5] flex items-center justify-center shrink-0">
              <Building2 className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{deadline.clientName}</p>
              <p className="text-[11px] text-muted-foreground">Open client →</p>
            </div>
          </button>
          <div className="flex items-center gap-3 p-2 -mx-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{formatFullDate(deadline.dueDate)}</p>
              <p className="text-[11px] text-muted-foreground">{formatDayLabel(deadline.dueDate)}</p>
            </div>
          </div>
          {deadline.recurrence !== 'none' && (
            <div className="flex items-center gap-3 p-2 -mx-2">
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{RECURRENCE_LABELS[deadline.recurrence]}</p>
                <p className="text-[11px] text-muted-foreground">Recurrence</p>
              </div>
            </div>
          )}
          {(deadline as any).penalty && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
              <p className="font-semibold text-amber-700 mb-1 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Late lodgement penalty
              </p>
              <p className="text-amber-800">{(deadline as any).penalty}</p>
            </div>
          )}
          {/* Completion details (only when status === 'completed') */}
          {deadline.status === 'completed' && ((deadline as any).lodgementReference || (deadline as any).completedAt) && (
            <div className="rounded-md border bg-muted/20 p-3 space-y-1.5">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Completion record</p>
              {(deadline as any).lodgementReference && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Lodgement reference</p>
                  <p className="text-sm font-mono">{(deadline as any).lodgementReference}</p>
                </div>
              )}
              {(deadline as any).completedAt && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Marked completed</p>
                  <p className="text-sm">{formatFullDate((deadline as any).completedAt)}</p>
                </div>
              )}
            </div>
          )}

          <Separator />
          <div className="space-y-2">
            {deadline.status !== 'completed' ? (
              <Button className="w-full" onClick={onComplete}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark as completed
              </Button>
            ) : (
              <Button variant="outline" className="w-full" onClick={onUncomplete} disabled={uncompleting}>
                {uncompleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
                Un-mark completed
              </Button>
            )}
            <Button variant="outline" className="w-full" onClick={onGoToClient}>
              <Building2 className="h-4 w-4 mr-2" />
              View Client
            </Button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default DeadlinesPage;
